import isNil from 'lodash/isNil';

import { findCustomValue } from './customValuesUtils';
import { CalendarDate, addCalendarDate, isAfter, subtractCalendarDate, toCalendarDate } from './dateutils';
import { makeLabels } from './labelsUtils';
import {
  CustomFieldOptionInput,
  CustomFieldType,
  NotificationType,
  RuleAttributeRefInput,
  RuleConditionInput,
  RuleConditionsOperator,
  RuleDurationUnit,
  RuleOutcomeInput,
  RuleOutcomeType,
  ScheduleRule,
  Technic,
  TechnicLibrary,
  TechnicLibraryCriticality,
  TechnicLibraryLocationScope,
  TechnicLibraryMaintenanceSchema,
  TechnicLibraryMaintenanceSchemaInput,
  TechnicLibraryMaintenanceType,
  TechnicLibraryRecurrence,
  TechnicLibraryResponsibleParty,
  TechnicLibraryTriggerType,
  TechnicLibraryType,
  TechnicLibraryValidity,
  TechnicLibraryValidityUnit,
  TechnicMaintenanceHistory,
  TechnicStatus,
  TechnicType,
  UnitType,
} from './types/model-app';
import { isMeter } from './utilityUtils';

export const MAINTENANCE_SCHEMA_FOREIGN_TABLE_NAME = 'TechnicLibraryMaintenanceSchema';

export const NEW_MAINTENANCE_HISTORY_ID_PREFIX = 'NEW-';

export const isNewMaintenanceHistoryId = (id?: string | null): boolean =>
  !isNil(id) && id.startsWith(NEW_MAINTENANCE_HISTORY_ID_PREFIX);

export type CleanedTechnicMaintenanceHistory = Pick<
  TechnicMaintenanceHistory,
  'fileId' | 'date' | 'contactId' | 'id' | 'maintenanceSchemaId' | 'customValues'
>;

export const cleanTechnicMaintenanceHistory = (
  history: TechnicMaintenanceHistory
): CleanedTechnicMaintenanceHistory => ({
  contactId: history.contactId,
  customValues: history.customValues?.map((customValue) => ({ name: customValue.name, value: customValue.value })),
  date: history.date,
  fileId: history.fileId,
  id: history.id,
  maintenanceSchemaId: history.maintenanceSchemaId,
});

const getLatestDate = (dates: string[]): string | null =>
  dates.reduce<string | null>(
    (latestDate, date) =>
      isNil(latestDate) || isAfter(toCalendarDate(date), toCalendarDate(latestDate)) ? date : latestDate,
    null
  );

const getHistoryDatesForSchema = (histories: TechnicMaintenanceHistory[], schemaId: string): string[] =>
  histories.reduce<string[]>((dates, history) => {
    if (history.maintenanceSchemaId === schemaId) {
      dates.push(history.date);
    }
    return dates;
  }, []);

const getUntaggedHistoryDates = (histories: TechnicMaintenanceHistory[]): string[] =>
  histories.reduce<string[]>((dates, history) => {
    if (isNil(history.maintenanceSchemaId)) {
      dates.push(history.date);
    }
    return dates;
  }, []);

export const getSchemaLastControlDate = (technic: Technic, schemaId: string): string | null => {
  const maintenanceHistory = technic.maintenanceHistory ?? [];
  const schemaDate = getLatestDate(getHistoryDatesForSchema(maintenanceHistory, schemaId));
  if (!isNil(schemaDate)) {
    return schemaDate;
  }
  return getLatestDate([
    ...getUntaggedHistoryDates(maintenanceHistory),
    ...(isNil(technic.lastControlDate) ? [] : [technic.lastControlDate]),
  ]);
};

export type TechnicLibraryMaintenanceSchemaWithRules = TechnicLibraryMaintenanceSchema & {
  rules?: ScheduleRule[] | null;
};

export type TechnicLibraryWithRules = Omit<TechnicLibrary, 'maintenanceSchemas'> & {
  maintenanceSchemas?: TechnicLibraryMaintenanceSchemaWithRules[] | null;
};

export const attachRulesToTechnicLibrary = (
  technicLibrary: TechnicLibrary,
  rules: ScheduleRule[]
): TechnicLibraryWithRules => ({
  ...technicLibrary,
  maintenanceSchemas: (technicLibrary.maintenanceSchemas ?? []).map((schema) => ({
    ...schema,
    rules: rules
      .filter(
        (rule) => rule.foreignKey === schema.id && rule.foreignTableName === MAINTENANCE_SCHEMA_FOREIGN_TABLE_NAME
      )
      .sort((first, second) => (first.order ?? 0) - (second.order ?? 0)),
  })),
});

export enum TechnicLibrarySystemAttribute {
  LAST_CONTROL_DATE = 'LAST_CONTROL_DATE',
  REGION = 'REGION',
}

export const isTechnicSystemAttribute = (attributeName: string): attributeName is TechnicLibrarySystemAttribute =>
  (Object.values(TechnicLibrarySystemAttribute) as string[]).includes(attributeName);

export const isCertificate = (type: TechnicLibraryType): boolean =>
  [TechnicLibraryType.CERTIFICATE, TechnicLibraryType.CERTIFICATE_EPB].includes(type);

export const isEpbUsefulForLease = (type?: UnitType | null): boolean =>
  !isNil(type) && type !== UnitType.PARKING && type !== UnitType.OTHER;

export const addValidityDuration = (date: CalendarDate, validity: TechnicLibraryValidity): CalendarDate => {
  switch (validity.validityUnit) {
    case TechnicLibraryValidityUnit.DAY:
      return addCalendarDate(date, validity.value, 'days');
    case TechnicLibraryValidityUnit.MONTH:
      return addCalendarDate(date, validity.value, 'months');
    default:
      return addCalendarDate(date, validity.value, 'years');
  }
};

export const subtractValidityDuration = (date: CalendarDate, validity: TechnicLibraryValidity): CalendarDate => {
  switch (validity.validityUnit) {
    case TechnicLibraryValidityUnit.DAY:
      return subtractCalendarDate(date, validity.value, 'days');
    case TechnicLibraryValidityUnit.MONTH:
      return subtractCalendarDate(date, validity.value, 'months');
    default:
      return subtractCalendarDate(date, validity.value, 'years');
  }
};

export enum TechnicCustomValue {
  CHIMNEY_UNIT_INVENTORY_ID = 'chimneyUnitInventoryId',
  DETECTOR_BRAND = 'detectorBrand',
  DETECTOR_SERIAL_NUMBER = 'detectorSerialNumber',
  DETECTOR_TYPE = 'detectorType',
  DETECTOR_UNIT_INVENTORY_ID = 'detectorUnitInventoryId',
  HEATING_BRAND = 'heatingBrand',
  HEATING_INSTALLATION_DATE = 'heatingInstallationDate',
  HEATING_MODEL = 'heatingModel',
  HEATING_TYPE = 'heatingType',
  PEB_ENERGETIC_PERFORMANCE = 'PebEnergeticPerformance',
  PEB_ISSUE_DATE = 'PebIssueDate',
  PEB_REPORT_NUMBER = 'PebReportNumber',
  PEB_VALIDITY_DATE = 'PebValidityDate',
  TANK_CAPACITY = 'tankCapacity',
  TANK_ENCUVEMENT = 'tankEncuvement',
  TANK_INSTALLATION_DATE = 'tankInstallationDate',
  TANK_LEAK_DETECTION = 'tankLeakDetection',
  TANK_STATE = 'tankState',
  TANK_TYPE = 'tankType',
  TANK_WALL_TYPE = 'tankWallType',
}

/**
 * Return all technics grouped by type. If leaseId is defined, it will return the lease technic and not the original.
 * @param technics
 * @param unitId
 * @param buildingId
 * @param leaseId
 * @returns An array with [PEB, smokeDetectors, fuelTanks, heating, chimneys, utilityProviders]
 */
export const getTechnicsByType = (
  technics: Technic[],
  unitId: string | undefined,
  buildingId: string | undefined,
  leaseId: string | undefined,
  isMainUnit?: boolean
): [Technic[], Technic[], Technic[], Technic[], Technic[], Technic[]] => {
  return technics.reduce(
    (acc: [Technic[], Technic[], Technic[], Technic[], Technic[], Technic[]], currentTechnic: Technic) => {
      const canBeAdded = (!leaseId && !currentTechnic.leaseId) || (leaseId && currentTechnic.leaseId === leaseId);
      if (!canBeAdded) {
        return acc;
      }
      if (
        (!isNil(currentTechnic.unit) && currentTechnic.unit.id === unitId) ||
        (!isNil(currentTechnic.building) && currentTechnic.building.id === buildingId)
      ) {
        // ** PEB **

        const updateWithMoreRecentEpb = (epbTechnic: Technic, index: number) => {
          const epbValidityDate = findCustomValue(epbTechnic.customValues, TechnicCustomValue.PEB_VALIDITY_DATE);
          const accEpbValidityDate = findCustomValue(acc[0][index]?.customValues, TechnicCustomValue.PEB_VALIDITY_DATE);

          if (
            !acc[0][index] ||
            (epbValidityDate &&
              accEpbValidityDate &&
              isAfter(toCalendarDate(epbValidityDate), toCalendarDate(accEpbValidityDate)))
          ) {
            acc[0][index] = epbTechnic;
          }
        };

        if (currentTechnic.type === TechnicType.PEB) {
          if (currentTechnic.status === TechnicStatus.ARCHIVED) {
            return acc;
          }
          if (currentTechnic.unit?.id === unitId) {
            updateWithMoreRecentEpb(currentTechnic, 0);
          } else if (isMainUnit && currentTechnic.buildingId === buildingId) {
            updateWithMoreRecentEpb(currentTechnic, 1);
          }
          return acc;
        }

        // ** Detector **
        if (currentTechnic.type === TechnicType.DETECTOR) {
          // Check if same technic on lease is already added so we can skip the original one
          const recordWithLeaseId = acc[1].find(
            (technic) => technic.originalTechnicId === currentTechnic.id && technic.leaseId === leaseId
          );
          if (recordWithLeaseId) {
            return acc;
          }
          // Check if currentTechnic is with leaseId and already present in acc
          // -> Replace the acc one with currentTechnic
          const sameRecordWithoutLeaseId = acc[1].findIndex(
            (technic) => technic.id === currentTechnic.originalTechnicId && currentTechnic.leaseId === leaseId
          );
          if (sameRecordWithoutLeaseId !== -1) {
            acc[1][sameRecordWithoutLeaseId] = currentTechnic;
            return acc;
          }
          acc[1].push(currentTechnic);
          return acc;
        }

        if (isMeter(currentTechnic) && currentTechnic.status === TechnicStatus.ARCHIVED && isNil(leaseId)) {
          return acc;
        }

        // ** Technic with maintenance field **
        let technicIndex = -1;
        if (currentTechnic.type === TechnicType.FUELTANK) {
          technicIndex = 2;
        } else if (currentTechnic.type === TechnicType.HEATING) {
          technicIndex = 3;
        } else if (currentTechnic.type === TechnicType.CHIMNEY) {
          technicIndex = 4;
        } else if (currentTechnic.type === TechnicType.UTILITY_PROVIDER) {
          technicIndex = 5;
        }

        if (technicIndex === -1) {
          // Should not happen except if we add type in db and not here -> Ignoring here
          return acc;
        }

        // Check if same technic on lease is already added so we can skip the original one
        const recordWithLeaseId = (acc[technicIndex] as Technic[]).find(
          (technic) => technic.originalTechnicId === currentTechnic.id
        );
        if (recordWithLeaseId) {
          return acc;
        }

        // Check if currentTechnic is with leaseId and already present in acc -> Replace the acc one with currentTechnic
        const sameRecordWithoutLeaseId = (acc[technicIndex] as Technic[]).findIndex(
          (technic) => technic.id === currentTechnic.originalTechnicId && currentTechnic.leaseId === leaseId
        );
        if (sameRecordWithoutLeaseId !== -1) {
          (acc[technicIndex] as Technic[])[sameRecordWithoutLeaseId] = currentTechnic;
          return acc;
        }
        (acc[technicIndex] as Technic[]).push(currentTechnic);
      }

      return acc;
    },
    [[], [], [], [], [], []]
  );
};
// I KNOW YOU MISS ME BUT STOP CRYING ME OUT LOUD PLEASE

export interface CustomFieldDef {
  fieldName: string;
  enLabel: string;
  frLabel: string;
  nlLabel: string;
  deLabel: string;
  fieldType: CustomFieldType;
  isRequired?: boolean;
  options?: CustomFieldOptionInput[];
}

export interface RuleSeedInput {
  conditions: RuleConditionInput[];
  conditionsOperator?: RuleConditionsOperator | null;
  outcome: RuleOutcomeInput;
}

export type TechnicLibraryMaintenanceSchemaDef = Omit<TechnicLibraryMaintenanceSchemaInput, 'rules' | 'id'> & {
  rules?: RuleSeedInput[];
};

export interface TechnicLibraryDef {
  enName: string;
  frName: string;
  nlName: string;
  deName: string;
  enDescription?: string;
  frDescription?: string;
  nlDescription?: string;
  deDescription?: string;
  format?: string;
  type: TechnicLibraryType;
  criticality: TechnicLibraryCriticality;
  locationScope: TechnicLibraryLocationScope;
  validity: { value: number; validityUnit: TechnicLibraryValidityUnit };
  maintenanceSchemas?: TechnicLibraryMaintenanceSchemaDef[];
  customFields: CustomFieldDef[];
}

export enum DetectorType {
  CO2 = 'CO2',
  SMOKE = 'SMOKE',
}

export enum TankType {
  AERIAL = 'AERIAL',
  UNDERGROUND = 'UNDERGROUND',
}

export enum TankState {
  EXISTING = 'EXISTING',
  NEW = 'NEW',
}

export enum TankWallType {
  DOUBLE = 'DOUBLE',
  SIMPLE = 'SIMPLE',
}

enum EnergeticPerformance {
  A_PLUS_PLUS = 'A++',
  A_PLUS = 'A+',
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
}

export const energeticPerformanceOptions: CustomFieldOptionInput[] = [
  EnergeticPerformance.A_PLUS_PLUS,
  EnergeticPerformance.A_PLUS,
  EnergeticPerformance.A,
  EnergeticPerformance.B,
  EnergeticPerformance.C,
  EnergeticPerformance.D,
  EnergeticPerformance.E,
  EnergeticPerformance.F,
  EnergeticPerformance.G,
].map((performance) => ({
  id: performance,
  labels: makeLabels(performance, performance, performance, performance),
}));

const LAST_CONTROL_DATE_ATTRIBUTE: RuleAttributeRefInput = {
  attributeName: TechnicLibrarySystemAttribute.LAST_CONTROL_DATE,
};

const DEFAULT_NOTIFY_LEAD_TIME_MONTHS = 3;
const HEATING_MAINTENANCE_INTERVAL_YEARS = 1;
const CHIMNEY_SWEEPING_INTERVAL_YEARS = 1;
const DETECTOR_BATTERY_INTERVAL_YEARS = 5;
const intervalOutcome = (years: number): RuleOutcomeInput => ({
  type: RuleOutcomeType.INTERVAL,
  interval: {
    referenceAttribute: LAST_CONTROL_DATE_ATTRIBUTE,
    interval: { value: years, unit: RuleDurationUnit.YEAR },
    notifyLeadTime: { value: DEFAULT_NOTIFY_LEAD_TIME_MONTHS, unit: RuleDurationUnit.MONTH },
  },
});
const rule = (conditions: RuleConditionInput[], outcome: RuleOutcomeInput): RuleSeedInput => ({
  conditions,
  outcome,
});

const fixedIntervalMaintenanceRules = (years: number): RuleSeedInput[] => [rule([], intervalOutcome(years))];

export const DEFAULT_TECHNIC_LIBRARY_DEFINITIONS: TechnicLibraryDef[] = [
  {
    enName: 'EPB Certificate',
    frName: 'Certificat PEB',
    nlName: 'EPC Certificaat',
    deName: 'Energieausweis',
    enDescription: 'Energy performance certificate documenting the energy efficiency of the property.',
    frDescription: "Certificat de performance énergétique documentant l'efficacité énergétique du bien.",
    nlDescription: 'Energieprestatiecertificaat dat de energiezuinigheid van het pand documenteert.',
    deDescription: 'Energieausweis, der die Energieeffizienz der Immobilie dokumentiert.',
    format: 'EPB_0001',
    type: TechnicLibraryType.CERTIFICATE_EPB,
    criticality: TechnicLibraryCriticality.HIGH,
    locationScope: TechnicLibraryLocationScope.BOTH,
    validity: { value: 10, validityUnit: TechnicLibraryValidityUnit.YEAR },
    customFields: [
      {
        fieldName: TechnicCustomValue.PEB_ENERGETIC_PERFORMANCE,
        enLabel: 'Energetic Performance',
        frLabel: 'Performance Énergétique',
        nlLabel: 'Energieprestatie',
        deLabel: 'Energieleistung',
        fieldType: CustomFieldType.DROPDOWN,
        isRequired: true,
        options: energeticPerformanceOptions,
      },
      {
        fieldName: TechnicCustomValue.PEB_VALIDITY_DATE,
        enLabel: 'Validity Date',
        frLabel: 'Date de Validité',
        nlLabel: 'Geldigheidsdatum',
        deLabel: 'Gültigkeitsdatum',
        fieldType: CustomFieldType.DATE,
        isRequired: true,
      },
      {
        fieldName: TechnicCustomValue.PEB_ISSUE_DATE,
        enLabel: 'Visit Date',
        frLabel: 'Date de Visite',
        nlLabel: 'Bezoekdatum',
        deLabel: 'Besuchsdatum',
        fieldType: CustomFieldType.DATE,
        isRequired: true,
      },
      {
        fieldName: TechnicCustomValue.PEB_REPORT_NUMBER,
        enLabel: 'Report Number',
        frLabel: 'Numéro de Rapport',
        nlLabel: 'Rapportnummer',
        deLabel: 'Berichtsnummer',
        fieldType: CustomFieldType.STRING,
        isRequired: true,
      },
    ],
  },
  {
    enName: 'Heating System',
    frName: 'Système de Chauffage',
    nlName: 'Verwarmingssysteem',
    deName: 'Heizsystem',
    enDescription: 'Heating installation including boiler, radiators, and distribution system.',
    frDescription: 'Installation de chauffage comprenant la chaudière, les radiateurs et le système de distribution.',
    nlDescription: 'Verwarmingsinstallatie inclusief ketel, radiatoren en distributiesysteem.',
    deDescription: 'Heizungsanlage einschließlich Kessel, Heizkörper und Verteilsystem.',
    format: 'HEATING_0001',
    type: TechnicLibraryType.TECHNIC,
    criticality: TechnicLibraryCriticality.HIGH,
    locationScope: TechnicLibraryLocationScope.BOTH,
    validity: { value: 15, validityUnit: TechnicLibraryValidityUnit.YEAR },
    maintenanceSchemas: [
      {
        triggerType: TechnicLibraryTriggerType.TIMEBASED,
        recurrence: TechnicLibraryRecurrence.PERIODIC,
        interval: { value: 1, validityUnit: TechnicLibraryValidityUnit.YEAR },
        maintenanceType: TechnicLibraryMaintenanceType.MAINTENANCE,
        names: makeLabels('Annual Maintenance', 'Entretien annuel', 'Jaarlijks onderhoud', 'Jährliche Wartung'),
        criticality: TechnicLibraryCriticality.HIGH,
        responsibleParty: TechnicLibraryResponsibleParty.OWNER,
        notificationType: NotificationType.HeatingCertificate,
        notificationsEnabled: true,
        rules: fixedIntervalMaintenanceRules(HEATING_MAINTENANCE_INTERVAL_YEARS),
      },
    ],
    customFields: [
      {
        fieldName: TechnicCustomValue.HEATING_BRAND,
        enLabel: 'Brand',
        frLabel: 'Marque',
        nlLabel: 'Merk',
        deLabel: 'Marke',
        fieldType: CustomFieldType.STRING,
      },
      {
        fieldName: TechnicCustomValue.HEATING_MODEL,
        enLabel: 'Model',
        frLabel: 'Modèle',
        nlLabel: 'Model',
        deLabel: 'Modell',
        fieldType: CustomFieldType.STRING,
      },
      {
        fieldName: TechnicCustomValue.HEATING_INSTALLATION_DATE,
        enLabel: 'Installation Date',
        frLabel: "Date d'Installation",
        nlLabel: 'Installatiedatum',
        deLabel: 'Installationsdatum',
        fieldType: CustomFieldType.DATE,
      },
      {
        fieldName: TechnicCustomValue.HEATING_TYPE,
        enLabel: 'Heating Type',
        frLabel: 'Type de Chauffage',
        nlLabel: 'Verwarmingstype',
        deLabel: 'Heizungstyp',
        fieldType: CustomFieldType.DROPDOWN,
        isRequired: true,
        options: [
          { id: 'GAS', labels: makeLabels('Gas', 'Gaz', 'Gas', 'Gas') },
          { id: 'FUEL_OIL', labels: makeLabels('Fuel Oil', 'Mazout', 'Stookolie', 'Heizöl') },
          { id: 'ELECTRICITY', labels: makeLabels('Electricity', 'Électricité', 'Elektriciteit', 'Elektrizität') },
          { id: 'HEATING_PUMP', labels: makeLabels('Heating Pump', 'Pompe à Chaleur', 'Warmtepomp', 'Wärmepumpe') },
          { id: 'WOOD', labels: makeLabels('Wood', 'Bois', 'Hout', 'Holz') },
          { id: 'GEOTHERMAL', labels: makeLabels('Geothermal', 'Géothermie', 'Geothermisch', 'Geothermie') },
          { id: 'SOLAR', labels: makeLabels('Solar', 'Solaire', 'Zonne-energie', 'Solar') },
          { id: 'WIND', labels: makeLabels('Wind', 'Éolien', 'Wind', 'Wind') },
        ],
      },
    ],
  },
  {
    enName: 'Chimney',
    frName: 'Cheminée',
    nlName: 'Schoorsteen',
    deName: 'Schornstein',
    enDescription: 'Chimney or flue system used for combustion gas evacuation.',
    frDescription: "Cheminée ou conduit de fumée utilisé pour l'évacuation des gaz de combustion.",
    nlDescription: 'Schoorsteen of rookgasafvoer voor de evacuatie van verbrandingsgassen.',
    deDescription: 'Schornstein oder Rauchgaskanal zur Ableitung von Verbrennungsgasen.',
    format: 'CHIMNEY_0001',
    type: TechnicLibraryType.TECHNIC,
    criticality: TechnicLibraryCriticality.HIGH,
    locationScope: TechnicLibraryLocationScope.BOTH,
    validity: { value: 20, validityUnit: TechnicLibraryValidityUnit.YEAR },
    maintenanceSchemas: [
      {
        triggerType: TechnicLibraryTriggerType.TIMEBASED,
        recurrence: TechnicLibraryRecurrence.PERIODIC,
        interval: { value: 1, validityUnit: TechnicLibraryValidityUnit.YEAR },
        maintenanceType: TechnicLibraryMaintenanceType.LEGAL_MAINTENANCE,
        names: makeLabels(
          'Annual Chimney Sweep',
          'Ramonage Annuel',
          'Jaarlijkse Schoorsteenveger',
          'Jährliche Schornsteinfegerung'
        ),
        criticality: TechnicLibraryCriticality.HIGH,
        responsibleParty: TechnicLibraryResponsibleParty.OWNER,
        notificationType: NotificationType.ChimneySweeping,
        notificationsEnabled: true,
        rules: fixedIntervalMaintenanceRules(CHIMNEY_SWEEPING_INTERVAL_YEARS),
      },
    ],
    customFields: [
      {
        fieldName: TechnicCustomValue.CHIMNEY_UNIT_INVENTORY_ID,
        enLabel: 'Room assigned',
        frLabel: 'Pièce attribuée',
        nlLabel: 'Toegewezen kamer',
        deLabel: 'Zugewiesenes Zimmer',
        fieldType: CustomFieldType.UNIT_INVENTORY,
      },
    ],
  },
  {
    enName: 'Detector',
    frName: 'Détecteur',
    nlName: 'Detector',
    deName: 'Detektor',
    enDescription: 'Smoke or CO2 detector installed for safety compliance.',
    frDescription: 'Détecteur de fumée ou de CO2 installé pour la conformité aux normes de sécurité.',
    nlDescription: 'Rook- of CO2-detector geïnstalleerd voor veiligheidsconformiteit.',
    deDescription: 'Rauch- oder CO2-Detektor für die Einhaltung von Sicherheitsvorschriften.',
    format: 'DETECTOR_0001',
    type: TechnicLibraryType.TECHNIC,
    criticality: TechnicLibraryCriticality.MEDIUM,
    locationScope: TechnicLibraryLocationScope.BOTH,
    validity: { value: 10, validityUnit: TechnicLibraryValidityUnit.YEAR },
    maintenanceSchemas: [
      {
        triggerType: TechnicLibraryTriggerType.TIMEBASED,
        recurrence: TechnicLibraryRecurrence.PERIODIC,
        interval: { value: 5, validityUnit: TechnicLibraryValidityUnit.YEAR },
        maintenanceType: TechnicLibraryMaintenanceType.MAINTENANCE,
        names: makeLabels(
          'Detector Battery Replacement',
          'Remplacement de la batterie du détecteur',
          'Vervanging van de detectorbatterij',
          'Austausch der Detektorbatterie'
        ),
        criticality: TechnicLibraryCriticality.MEDIUM,
        responsibleParty: TechnicLibraryResponsibleParty.OWNER,
        notificationType: NotificationType.SmokeDetectorBattery,
        notificationsEnabled: true,
        rules: fixedIntervalMaintenanceRules(DETECTOR_BATTERY_INTERVAL_YEARS),
      },
    ],
    customFields: [
      {
        fieldName: TechnicCustomValue.DETECTOR_TYPE,
        enLabel: 'Detector Type',
        frLabel: 'Type de Détecteur',
        nlLabel: 'Detectortype',
        deLabel: 'Detektortyp',
        fieldType: CustomFieldType.DROPDOWN,
        isRequired: true,
        options: [
          { id: 'CO2', labels: makeLabels('CO2', 'CO2', 'CO2', 'CO2') },
          { id: 'SMOKE', labels: makeLabels('Smoke', 'Fumée', 'Rook', 'Rauch') },
        ],
      },
      {
        fieldName: TechnicCustomValue.DETECTOR_SERIAL_NUMBER,
        enLabel: 'Serial Number',
        frLabel: 'Numéro de Série',
        nlLabel: 'Serienummer',
        deLabel: 'Seriennummer',
        fieldType: CustomFieldType.STRING,
        isRequired: false,
      },
      {
        fieldName: TechnicCustomValue.DETECTOR_BRAND,
        enLabel: 'Brand',
        frLabel: 'Marque',
        nlLabel: 'Merk',
        deLabel: 'Marke',
        fieldType: CustomFieldType.STRING,
        isRequired: true,
      },
      {
        fieldName: TechnicCustomValue.DETECTOR_UNIT_INVENTORY_ID,
        enLabel: 'Room assigned',
        frLabel: 'Pièce attribuée',
        nlLabel: 'Toegewezen kamer',
        deLabel: 'Zugewiesenes Zimmer',
        fieldType: CustomFieldType.UNIT_INVENTORY,
      },
    ],
  },
  {
    enName: 'Fuel Tank',
    frName: 'Citerne',
    nlName: 'Brandstoftank',
    deName: 'Heizöltank',
    enDescription: 'Fuel storage tank for heating oil supply.',
    frDescription: "Citerne de stockage de mazout pour l'alimentation du système de chauffage.",
    nlDescription: 'Brandstofopslagtank voor de bevoorrading van het verwarmingssysteem.',
    deDescription: 'Heizölspeichertank für die Versorgung der Heizungsanlage.',
    format: 'TANK_0001',
    type: TechnicLibraryType.TECHNIC_TANK,
    criticality: TechnicLibraryCriticality.HIGH,
    locationScope: TechnicLibraryLocationScope.BOTH,
    validity: { value: 25, validityUnit: TechnicLibraryValidityUnit.YEAR },
    customFields: [
      {
        fieldName: TechnicCustomValue.TANK_TYPE,
        enLabel: 'Tank Type',
        frLabel: 'Type de Citerne',
        nlLabel: 'Tanktype',
        deLabel: 'Tanktyp',
        fieldType: CustomFieldType.DROPDOWN,
        isRequired: true,
        options: [
          { id: 'AERIAL', labels: makeLabels('Aerial', 'Aérien', 'Bovengronds', 'Oberirdisch') },
          { id: 'UNDERGROUND', labels: makeLabels('Underground', 'Souterrain', 'Ondergronds', 'Unterirdisch') },
        ],
      },
      {
        fieldName: TechnicCustomValue.TANK_CAPACITY,
        enLabel: 'Capacity',
        frLabel: 'Capacité',
        nlLabel: 'Capaciteit',
        deLabel: 'Kapazität',
        fieldType: CustomFieldType.NUMBER,
        isRequired: true,
      },
      {
        fieldName: TechnicCustomValue.TANK_STATE,
        enLabel: 'Tank State',
        frLabel: 'État de la Citerne',
        nlLabel: 'Tankstatus',
        deLabel: 'Tankzustand',
        fieldType: CustomFieldType.DROPDOWN,
        isRequired: true,
        options: [
          { id: 'NEW', labels: makeLabels('New', 'Neuf', 'Nieuw', 'Neu') },
          { id: 'EXISTING', labels: makeLabels('Existing', 'Existant', 'Bestaand', 'Bestehend') },
        ],
      },
      {
        fieldName: TechnicCustomValue.TANK_WALL_TYPE,
        enLabel: 'Wall Type',
        frLabel: 'Type de Paroi',
        nlLabel: 'Wandtype',
        deLabel: 'Wandtyp',
        fieldType: CustomFieldType.DROPDOWN,
        options: [
          { id: 'SIMPLE', labels: makeLabels('Simple', 'Simple', 'Enkel', 'Einfach') },
          { id: 'DOUBLE', labels: makeLabels('Double', 'Double', 'Dubbel', 'Doppelt') },
        ],
      },
      {
        fieldName: TechnicCustomValue.TANK_LEAK_DETECTION,
        enLabel: 'Leak Detection',
        frLabel: 'Détection de Fuite',
        nlLabel: 'Lekdetectie',
        deLabel: 'Leckerkennung',
        fieldType: CustomFieldType.BOOLEAN,
      },
      {
        fieldName: TechnicCustomValue.TANK_ENCUVEMENT,
        enLabel: 'Encuvement',
        frLabel: 'Encuvement',
        nlLabel: 'Inkuiping',
        deLabel: 'Auffangwanne',
        fieldType: CustomFieldType.BOOLEAN,
      },
      {
        fieldName: TechnicCustomValue.TANK_INSTALLATION_DATE,
        enLabel: 'Installation Date',
        frLabel: "Date d'Installation",
        nlLabel: 'Installatiedatum',
        deLabel: 'Installationsdatum',
        fieldType: CustomFieldType.DATE,
      },
    ],
  },
];
